import { createListenerMiddleware } from '@reduxjs/toolkit'
import { RootState } from 'redux/store'
import configSlice from './config-slice'
import MParticle from 'modules/mParticle'
import orderCurrencySlice from 'modules/order/state/currency/order-currency-slice'
import { paymentMethodsApi } from 'redux/services/paymentMethods'

export const configMiddleware = createListenerMiddleware<RootState>()

/**
 * Middleware that listens for store initialization and handles mParticle device ID
 * when the app is not embedded.
 *
 * This middleware:
 * 1. Listens for any action that indicates the store has been initialized with config
 * 2. Checks if config.isEmbedded is false and mParticle.deviceId is empty
 * 3. If so, calls MParticle.getDeviceId() and dispatches setMParticleConfig
 *
 * When config.isEmbedded is true, the device ID is handled by SDK messages
 * in use-sdk-safeguard-token-checker.ts, so this middleware does nothing.
 */
configMiddleware.startListening({
  predicate: (_action, currentState) => {
    // Listen for when config becomes available (store initialization)
    const hasConfig = !!currentState?.config

    return hasConfig
  },
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState()
    const config = state.config

    // Only set device ID if not embedded and device ID is not already set
    if (!config.isEmbedded && !config.mParticle?.deviceId) {
      try {
        const deviceId = MParticle.getDeviceId()

        listenerApi.dispatch(
          configSlice.actions.setMParticleConfig({
            deviceId,
          }),
        )
      } catch (error) {
        console.error('Failed to get mParticle device ID:', error)
      }
    }
  },
})

// Middleware that listens for currency changes and updates active payment methods
configMiddleware.startListening({
  actionCreator: orderCurrencySlice.actions.setValue,
  effect: async (action, listenerApi) => {
    const { config } = listenerApi.getState()

    try {
      // Fetch payment methods for the new currency
      const result = await listenerApi.dispatch(
        paymentMethodsApi.endpoints.getPaymentMethods.initiate({
          campaignId: config.campaignId,
          currency: action.payload,
        }),
      )

      // If the fetch was successful, update the active payment methods
      if (result.data?.paymentMethods) {
        listenerApi.dispatch(
          configSlice.actions.setActivePaymentMethods(result.data.paymentMethods),
        )
      }
    } catch (error) {
      console.error('Failed to fetch payment methods for currency change:', error)
    }
  },
})
